// PdfViewer.jsx
import React, { useCallback, useState } from 'react';
import { Viewer, Worker, SpecialZoomLevel } from '@react-pdf-viewer/core';
import { pageNavigationPlugin } from '@react-pdf-viewer/page-navigation';
import { zoomPlugin } from '@react-pdf-viewer/zoom';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/page-navigation/lib/styles/index.css';
import '@react-pdf-viewer/zoom/lib/styles/index.css';
import DownloadIcon from '../../SVG/DownloadIcon';
import { useAuth } from "../../src/Context/AuthContext"

function buildUrl(path) {
    return new URL(String(path).replace(/^\//, ''), document.baseURI).toString();
}

export default function PdfViewer({ src, fileId, fileName, serviceForm }) {
    //State Variables 
    const [pageCount, setPageCount] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageInput, setPageInput] = useState('1');
    const [zoomPct, setZoomPct] = useState(100);
    const { logout } = useAuth();
    const workerUrl = 'https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js';

    // Plugins
    const zoomPluginInstance = zoomPlugin();
    const { ZoomIn, ZoomOut } = zoomPluginInstance;

    const pageNavPluginInstance = pageNavigationPlugin();
    const { GoToNextPage, GoToPreviousPage, jumpToPage } = pageNavPluginInstance;

    // ENV Variables
    const apiURL = import.meta.env.VITE_API;

    // Helper: trigger a download for what's already loaded in `src`
    const saveBlobOrUrl = useCallback(async () => {
        // If `src` is already a Blob URL (blob:...) or data URL, we can download directly
        const link = document.createElement('a');
        link.style.display = 'none';

        // If src is an http(s) URL, some browsers will navigate instead of "download".
        // If you need a *forced* download, fetch it as a blob first:
        if (typeof src === 'string' && /^https?:\/\//i.test(src)) {
            try {
                const resp = await fetch(src, { credentials: 'include' });
                if (!resp.ok) throw new Error('Fetch failed');
                const blob = await resp.blob();
                const objUrl = URL.createObjectURL(blob);
                link.href = objUrl;
                link.download = fileName || `document-${fileId}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(objUrl);
                return;
            } catch {
                // fall back to plain href navigation
                link.href = src;
                link.download = fileName || `document-${fileId}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                return;
            }
        }

        // blob: URL, data: URL, or same-origin file URL
        link.href = src;
        link.download = fileName || `document-${fileId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, [src, fileId, fileName]);

    // Download: authorize (which logs) -> only on 200 proceed to save
    const handleDownload = useCallback(async () => {
        try {
            if (serviceForm) {
                await saveBlobOrUrl();
                return;
            }

            const res = await fetch(`${apiURL}/image/pdf/authorize-download?documentID=${fileId}`, {
                method: 'POST',
                credentials: 'include',
            });

            if (res.status === 401) {
                logout();
                navigate('/login');
                throw new Error("unauthorized");
            }

            if (res.status === 403) {
                navigate('/home');
                throw new Error("forbidden");
            }

            if (!res.ok) {
                // Block download if server didn’t authorize/log successfully
                console.error('Download blocked by server (auth/log failed).');
                // Optionally parse JSON error for message:
                try {
                    const err = await res.json();
                    alert(err?.message || 'Download not authorized.');
                } catch {
                    alert('Download not authorized.');
                }
                return;
            }

            await saveBlobOrUrl();
        } catch (err) {
            console.error('Authorize-download error:', err);
            alert('Unable to authorize download.');
        }
    }, [apiURL, fileId, saveBlobOrUrl]);

    // Helpers for page entry
    const clamp = (n, min, max) => Math.max(min, Math.min(max, n || 1));
    const commitPageInput = useCallback(() => {
        const n = clamp(parseInt(pageInput, 10), 1, pageCount || 1);
        setPageInput(String(n));
        jumpToPage(n - 1); // 0-based
    }, [pageInput, pageCount, jumpToPage]);

    return (
        <div className="pdf-iframe">
            {/* Toolbar */}
            <div className="pdf-toolbar">
                <div className="pdf-title">{fileName}</div>

                {/* Zoom group */}
                <div className="pdf-group">
                    <ZoomOut>
                        {(p) => (
                            <button
                                type="button"
                                className="pdf-iconbtn"
                                onClick={p.onClick}
                                title="Zoom out"
                                aria-label="Zoom out"
                            >
                                −
                            </button>
                        )}
                    </ZoomOut>

                    <div className="pdf-zoom-val" aria-label="Zoom percent">
                        {zoomPct}%
                    </div>

                    <ZoomIn>
                        {(p) => (
                            <button
                                type="button"
                                className="pdf-iconbtn"
                                onClick={p.onClick}
                                title="Zoom in"
                                aria-label="Zoom in"
                            >
                                +
                            </button>
                        )}
                    </ZoomIn>
                </div>

                {/* Page group */}
                <div className="pdf-group">
                    <GoToPreviousPage>
                        {(p) => (
                            <button
                                type="button"
                                className="pdf-iconbtn"
                                onClick={p.onClick}
                                disabled={p.isDisabled}
                                title="Previous page"
                                aria-label="Previous page"
                            >
                                ‹
                            </button>
                        )}
                    </GoToPreviousPage>

                    <div>
                        <input
                            className="pdf-page-input"
                            value={pageInput}
                            onChange={(e) => setPageInput(e.target.value.replace(/[^\d]/g, ''))}
                            onBlur={commitPageInput}
                            onKeyDown={(e) => e.key === 'Enter' && commitPageInput()}
                            aria-label="Current page"
                        />
                        <span className="pdf-page-total">/ {pageCount || 1}</span>
                    </div>

                    <GoToNextPage>
                        {(p) => (
                            <button
                                type="button"
                                className="pdf-iconbtn"
                                onClick={p.onClick}
                                disabled={p.isDisabled}
                                title="Next page"
                                aria-label="Next page"
                            >
                                ›
                            </button>
                        )}
                    </GoToNextPage>
                </div>

                {/* Download */}
                <button
                    type="button"
                    className="pdf-download-btn"
                    onClick={handleDownload}
                    title="Download"
                    aria-label="Download"
                >
                    <DownloadIcon />
                </button>
            </div>

            {/* Viewer */}
            <div className="pdf-viewer-frame">
                <Worker workerUrl={workerUrl}>
                    <Viewer
                        fileUrl={src}
                        defaultScale={SpecialZoomLevel.PageWidth}
                        plugins={[zoomPluginInstance, pageNavPluginInstance]}
                        onDocumentLoad={(e) => {
                            const total = e.doc?.numPages || 1;
                            setPageCount(total);
                            setCurrentPage(1);
                            setPageInput('1');
                        }}
                        onPageChange={(e) => {
                            const next = (e.currentPage ?? 0) + 1;
                            setCurrentPage(next);
                            setPageInput(String(next));
                        }}
                        onZoom={(e) => {
                            setZoomPct(Math.round((e.scale || 1) * 100));
                        }}
                    />
                </Worker>
            </div>
        </div>
    );
}
